import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Icon from '../common/icons';
import { TOUR_EVENT } from './annaBus';
import { GUIDE_BY_ID } from './guides';
import './globalSearch.css';

const PAD = 8;
const GAP = 14;
const EDGE = 12;
const CARD_WIDTH = 330;
const DEFAULT_WAIT_MS = 5000;
const MOVE_MS = 420;
const SETTLE_MS = 900;

/** On screen and not inside a closed drawer, the palette or the tour itself. */
const usable = (element) => {
  if (element.closest('.gs-tour, .gs-layer, [aria-hidden="true"]')) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2 || rect.right <= 0 || rect.left >= window.innerWidth) return false;
  const style = window.getComputedStyle(element);
  return style.visibility !== 'hidden' && style.display !== 'none';
};

/** A step's target: a selector, a list of selectors tried in order, or { text, tag } for a labelled control. */
export const findTarget = (target) => {
  const options = Array.isArray(target) ? target : [target];
  for (const option of options) {
    if (typeof option === 'string') {
      const found = Array.from(document.querySelectorAll(option)).find(usable);
      if (found) return found;
    } else if (option?.text) {
      const wanted = option.text.toLowerCase();
      const found = Array.from(document.querySelectorAll(option.tag || 'button, a, [role="tab"]'))
        .filter(usable)
        .find((element) => element.textContent.replace(/\s+/g, ' ').trim().toLowerCase().startsWith(wanted));
      if (found) return found;
    }
  }
  return null;
};

const onPath = (location, path) => {
  const url = new URL(path, window.location.origin);
  if (url.pathname !== location.pathname) return false;
  const current = new URLSearchParams(location.search);
  return Array.from(url.searchParams).every(([key, value]) => current.get(key) === value);
};

const boxOf = (element) => {
  const box = element.getBoundingClientRect();
  return { top: box.top, left: box.left, width: box.width, height: box.height, bottom: box.bottom, right: box.right };
};

const inView = (box) => box.top >= 0 && box.bottom <= window.innerHeight;

/** The lit-up area around the target, kept inside the window. */
const spotFor = (rect) => {
  if (!rect) return null;
  const top = Math.max(6, rect.top - PAD);
  const left = Math.max(6, rect.left - PAD);
  const bottom = Math.min(window.innerHeight - 6, rect.bottom + PAD);
  const right = Math.min(window.innerWidth - 6, rect.right + PAD);
  return { top, left, bottom, right, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
};

/** Where the step card goes: below the target, else above, else beside it, else at the bottom. */
const placeCard = (spot, cardHeight) => {
  // Phones: the card spans the screen, so it never sits off-centre.
  const width = window.innerWidth < 480 ? window.innerWidth - EDGE * 2 : Math.min(CARD_WIDTH, window.innerWidth - EDGE * 2);
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!spot) return { top: Math.max(EDGE, (vh - cardHeight) / 2), left: (vw - width) / 2, width };
  const clampX = (x) => Math.min(Math.max(EDGE, x), vw - width - EDGE);
  const clampY = (y) => Math.min(Math.max(EDGE, y), vh - cardHeight - EDGE);
  if (vh - spot.bottom >= cardHeight + GAP + EDGE) return { top: spot.bottom + GAP, left: clampX(spot.left), width };
  if (spot.top >= cardHeight + GAP + EDGE) return { top: spot.top - GAP - cardHeight, left: clampX(spot.left), width };
  if (vw - spot.right >= width + GAP + EDGE) return { top: clampY(spot.top), left: spot.right + GAP, width };
  if (spot.left >= width + GAP + EDGE) return { top: clampY(spot.top), left: spot.left - GAP - width, width };
  return { top: vh - cardHeight - EDGE, left: (vw - width) / 2, width };
};

const sameRect = (a, b) => a && b && ['top', 'left', 'width', 'height'].every((key) => Math.abs(a[key] - b[key]) < 0.5);

const MISSING_NOTE = 'This part isn’t on screen right now. It shows up once there is data, or on a wider screen.';

/**
 * Guided tours: opens each step's page, waits for the part it points at, lights it up
 * and explains it in a card. Between steps the light and the card stay where they are
 * and then glide to the next part, so nothing blinks out while a page loads.
 * Started from a guide with startTour(id).
 *
 * `guides` maps ids to guides (the admin guides by default). A guide's `tour` is a list
 * of steps, or a function that returns one when the tour starts. A step can also have
 * `enter()` (runs when the step opens; may return a function that runs when it is left)
 * and `demo` (shown in the card). A guide's `onDone` runs when Done is pressed, in place
 * of the closing toast, and `doneLabel` renames Done; its `onEnd` runs whenever the tour
 * closes (Done, the X or Esc), before `onDone`. `blockPage` keeps clicks off the page
 * while the tour is open.
 */
const TourLayer = ({ guides = GUIDE_BY_ID, blockPage = false, missingNote = MISSING_NOTE }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const [tour, setTour] = useState(null);
  // The step whose part is lit up and whose text the card shows (lags `tour.index` while seeking).
  const [shown, setShown] = useState(null);
  const [rect, setRect] = useState(null);
  const [moving, setMoving] = useState(false);
  const [slow, setSlow] = useState(false);
  const [cardHeight, setCardHeight] = useState(180);
  const targetRef = useRef(null);
  const navigatedFor = useRef(null);
  const cardRef = useRef(null);
  const nextRef = useRef(null);
  const tourRef = useRef(null);
  tourRef.current = tour;

  const guide = tour ? guides[tour.id] : null;
  const steps = tour?.steps || [];
  const step = tour ? steps[tour.index] : null;
  const stepKey = tour ? `${tour.id}:${tour.index}:${tour.started}` : '';
  const shownStep = shown ? steps[shown.index] : null;
  const seeking = Boolean(tour) && (!shown || shown.key !== stepKey);

  useEffect(() => {
    const onTour = (event) => {
      const id = event.detail?.id;
      const found = guides[id];
      const list = typeof found?.tour === 'function' ? found.tour() : found?.tour;
      if (!list?.length) return;
      setShown(null);
      setRect(null);
      setTour({ id, index: 0, started: Date.now(), steps: list });
    };
    window.addEventListener(TOUR_EVENT, onTour);
    return () => window.removeEventListener(TOUR_EVENT, onTour);
  }, [guides]);

  // A step's own setup (open the chat, a menu…) and its undo when the step is left.
  useEffect(() => {
    if (!step?.enter) return undefined;
    const leave = step.enter();
    return typeof leave === 'function' ? leave : undefined;
    // Runs once per step: the step key covers the step itself.
  }, [stepKey]);

  // Reveal a step: glide the light and card from where they were to the new part.
  // A step without a target is a centred card, not a missing part.
  const reveal = useCallback((element, key, index, wanted = true) => {
    targetRef.current = element;
    setRect(element ? boxOf(element) : null);
    setShown({ key, index, missing: wanted && !element });
    setMoving(true);
    setSlow(false);
  }, []);

  useEffect(() => {
    if (!moving) return undefined;
    const timer = setTimeout(() => setMoving(false), MOVE_MS);
    return () => clearTimeout(timer);
  }, [moving, shown]);

  // Open the step's page if needed, then look for its part; scroll it into view and wait
  // for the scroll to settle before moving the light, so the light doesn't chase it.
  useEffect(() => {
    if (!step) return undefined;
    targetRef.current = null;
    if (step.path && !onPath(location, step.path) && navigatedFor.current !== stepKey) {
      navigatedFor.current = stepKey;
      navigate(step.path);
      return undefined;
    }
    const started = Date.now();
    const wait = step.wait || DEFAULT_WAIT_MS;
    let timer;
    let frame;
    const slowTimer = setTimeout(() => setSlow(true), 700);
    const settle = (element, last, stableFrames, since) => {
      const box = boxOf(element);
      const stable = sameRect(box, last) ? stableFrames + 1 : 0;
      if (stable >= 3 || Date.now() - since > SETTLE_MS) {
        reveal(element, stepKey, tour.index);
        return;
      }
      frame = requestAnimationFrame(() => settle(element, box, stable, since));
    };
    const look = () => {
      const element = step.target ? findTarget(step.target) : null;
      if (element) {
        if (!inView(boxOf(element))) {
          element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
        frame = requestAnimationFrame(() => settle(element, null, 0, Date.now()));
        return;
      }
      if (!step.target || Date.now() - started > wait) {
        reveal(null, stepKey, tour.index, Boolean(step.target));
        return;
      }
      timer = setTimeout(look, 120);
    };
    timer = setTimeout(look, 60);
    return () => {
      clearTimeout(timer);
      clearTimeout(slowTimer);
      cancelAnimationFrame(frame);
    };
    // location is read through pathname and search; the step key covers the step itself.
  }, [stepKey, location.pathname, location.search]);

  // Once a part is lit, follow it exactly as the page scrolls or resizes (no easing).
  useEffect(() => {
    if (!tour) return undefined;
    let frame;
    const tick = () => {
      const element = targetRef.current;
      if (element && element.isConnected) {
        const next = boxOf(element);
        setRect((previous) => (sameRect(previous, next) ? previous : next));
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [tour]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [shown, slow]);

  // Keep the keyboard on the card as each step appears (a step may open a menu that takes focus).
  useEffect(() => {
    if (shown) nextRef.current?.focus({ preventScroll: true });
  }, [shown?.key]);

  const finish = useCallback((done) => {
    const finished = tourRef.current && guides[tourRef.current.id];
    setTour(null);
    setShown(null);
    setRect(null);
    targetRef.current = null;
    if (finished?.onEnd) finished.onEnd();
    if (!done) return;
    if (finished?.onDone) finished.onDone();
    else toast.success('That’s the tour. Guides in the top bar has the rest.');
  }, [guides]);
  const end = useCallback(() => finish(false), [finish]);
  const go = useCallback((delta) => {
    const current = tourRef.current;
    if (!current) return;
    const index = current.index + delta;
    if (index >= current.steps.length) {
      finish(true);
      return;
    }
    if (index < 0) return;
    setTour({ ...current, index });
  }, [finish]);
  const next = useCallback(() => {
    if (seeking) return;
    if (shownStep?.clickOnNext && targetRef.current && !shown?.missing) targetRef.current.click();
    go(1);
  }, [seeking, shownStep, shown, go]);

  useEffect(() => {
    if (!tour) return undefined;
    const onKey = (event) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(event.target?.tagName) || event.target?.isContentEditable;
      if (event.key === 'Escape') end();
      else if (!typing && event.key === 'ArrowRight') next();
      else if (!typing && event.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tour, end, next, go]);

  const spot = spotFor(shown && !shown.missing ? rect : null);
  const card = placeCard(spot, cardHeight);
  const last = tour ? tour.index === steps.length - 1 : false;
  const animate = moving && !reduceMotion;

  return createPortal(
    <AnimatePresence>
      {tour && (
        <motion.div
          key="tour"
          className={`gs-tour${animate ? ' is-moving' : ''}${seeking ? ' is-seeking' : ''}${blockPage ? ' is-blocking' : ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.2 }}
        >
          {/* With nothing to light, the spot shrinks to the middle and only the dim stays. */}
          <div
            className={`gs-tour-spot${spot ? '' : ' is-empty'}`}
            style={spot
              ? { top: spot.top, left: spot.left, width: spot.width, height: spot.height }
              : { top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0 }}
          />
          {!shown ? (
            <div className="gs-tour-seeking" role="status">
              <span className="gs-spinner" aria-hidden="true" /> Opening the tour…
            </div>
          ) : (
            <div
              ref={cardRef}
              className="gs-tour-card"
              role="dialog"
              aria-modal={blockPage}
              aria-labelledby="gs-tour-title"
              style={{ top: card.top, left: card.left, width: card.width }}
            >
              <div className="gs-tour-top">
                <span className="gs-tour-kicker"><Icon name="book-open" /> {guide?.title}</span>
                <button type="button" className="gs-icon-btn" onClick={end} aria-label="End tour"><Icon name="x" /></button>
              </div>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={shown.index}
                  initial={reduceMotion ? false : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: reduceMotion ? 0 : 0.14 }}
                >
                  <h3 id="gs-tour-title" className="gs-tour-title">{shownStep.title}</h3>
                  <p className="gs-tour-body">{shownStep.body}</p>
                  {shownStep.demo && <div className="gs-tour-demo" aria-hidden="true">{shownStep.demo}</div>}
                  {shown.missing && (
                    <p className="gs-tour-note"><Icon name="info" /> {missingNote}</p>
                  )}
                  {shownStep.clickOnNext && !shown.missing && <p className="gs-tour-note"><Icon name="cursor" /> Next opens it for you.</p>}
                </motion.div>
              </AnimatePresence>
              <div className="gs-tour-foot">
                <span className="gs-tour-dots" aria-label={`Step ${tour.index + 1} of ${steps.length}`}>
                  {steps.map((_, index) => (
                    <span key={index} className={index === tour.index ? 'is-on' : index < tour.index ? 'is-done' : ''} />
                  ))}
                </span>
                <span className="gs-grow" />
                {seeking && slow && <span className="gs-tour-wait"><span className="gs-spinner" aria-hidden="true" /> Opening…</span>}
                {tour.index > 0 && !seeking && <button type="button" className="gs-btn is-ghost" onClick={() => go(-1)}>Back</button>}
                <button ref={nextRef} type="button" className="gs-btn is-dark" onClick={next} aria-busy={seeking} autoFocus>
                  {last ? guide?.doneLabel || 'Done' : 'Next'} {!last && <Icon name="arrow-right" />}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default TourLayer;
